/**
 * src/help/HelpProvider.tsx — config-driven, offline-first help store (WO-H1).
 *
 * - Ships DEFAULT_HELP_BUNDLE so every help surface renders with ZERO network (invariant: offline-first).
 * - Hydrates per-tenant overrides from GET /api/help-content when reachable, and merges them OVER the
 *   defaults by id. The fetch is non-blocking and fail-soft: if the endpoint is absent/offline/invalid,
 *   the defaults stand. Help never breaks because the network did.
 * - Tracks first-run coachmark "seen" flags via a platform-split store (secure-store / localStorage).
 *
 * Mount once near the app root, INSIDE QueryClientProvider (see app/_layout.tsx in MERGE-GUIDE-H1.md).
 * NEVER imports @smartbox/* (mobile invariant 1).
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  HelpContentResponse,
  type HelpContentBundle,
  type HelpEntry,
  type HelpGuide,
  type Coachmark,
} from '@beam/api-types';
import { get } from '../api/client';
import { DEFAULT_HELP_BUNDLE } from './content';
import { getSeenFlows, markFlowSeen, resetSeenFlows } from './seenStore';

// --- merge: overrides win by id, new ids appended -------------------------------------------
function mergeById<T extends { id: string }>(base: T[], over: T[]): T[] {
  if (!over.length) return base;
  const map = new Map(base.map((x) => [x.id, x]));
  for (const o of over) map.set(o.id, o);
  return Array.from(map.values());
}

function mergeBundle(base: HelpContentBundle, over: HelpContentBundle | null): HelpContentBundle {
  if (!over) return base;
  return {
    version: over.version || base.version,
    locale: over.locale || base.locale,
    entries: mergeById(base.entries, over.entries),
    coachmarks: mergeById(base.coachmarks, over.coachmarks),
    guides: mergeById(base.guides, over.guides),
  };
}

export interface CoachmarkFlow {
  flowKey: string;
  steps: Coachmark[];
  seen: boolean;
}

interface HelpContextValue {
  bundle: HelpContentBundle;
  /** Most-specific entry for a screen: a stateKey match beats a screen-only match. */
  getEntry: (screenKey: string, stateKey?: string) => HelpEntry | undefined;
  getEntryById: (id: string) => HelpEntry | undefined;
  getRelated: (entry: HelpEntry) => HelpEntry[];
  guides: HelpGuide[];
  searchEntries: (q: string) => HelpEntry[];
  /** Ordered steps + whether this flow has already been seen/dismissed. */
  getFlow: (flowKey: string) => CoachmarkFlow;
  markSeen: (flowKey: string) => void;
  /** Replay all first-run tours (Help Center action). */
  resetSeen: () => void;
  /** True until the persisted "seen" set has loaded — coachmarks wait on this to avoid a flash. */
  seenLoaded: boolean;
}

const HelpContext = createContext<HelpContextValue | null>(null);

const HELP_LOCALE = 'en';

export function HelpProvider({ children }: { children: React.ReactNode }) {
  const [seen, setSeen] = useState<string[]>([]);
  const [seenLoaded, setSeenLoaded] = useState(false);

  // Load persisted "seen" flags once. Failure → empty set (tour may replay; harmless).
  useEffect(() => {
    let alive = true;
    getSeenFlows()
      .then((flows) => {
        if (alive) setSeen(flows);
      })
      .finally(() => {
        if (alive) setSeenLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Fetch tenant overrides — fail-soft. Defaults render immediately regardless.
  const overrides = useQuery({
    queryKey: ['help-content', HELP_LOCALE],
    queryFn: async (): Promise<HelpContentBundle | null> => {
      const raw = await get<unknown>(`/api/help-content?locale=${HELP_LOCALE}`);
      const parsed = HelpContentResponse.safeParse(raw);
      return parsed.success ? parsed.data : null;
    },
    retry: false,
    staleTime: 5 * 60_000,
    // The provider mounts inside an authed shell; if the endpoint 404s pre-backend, the error is swallowed.
    throwOnError: false,
  });

  const bundle = useMemo(
    () => mergeBundle(DEFAULT_HELP_BUNDLE, overrides.data ?? null),
    [overrides.data],
  );

  const value = useMemo<HelpContextValue>(() => {
    const byId = new Map(bundle.entries.map((e) => [e.id, e]));
    return {
      bundle,
      getEntry: (screenKey, stateKey) => {
        const forScreen = bundle.entries.filter((e) => e.screenKey === screenKey);
        if (stateKey) {
          const exact = forScreen.find((e) => e.stateKey === stateKey);
          if (exact) return exact;
        }
        return forScreen.find((e) => !e.stateKey) ?? forScreen[0];
      },
      getEntryById: (id) => byId.get(id),
      getRelated: (entry) => (entry.relatedIds ?? []).map((id) => byId.get(id)).filter((x): x is HelpEntry => !!x),
      guides: bundle.guides,
      searchEntries: (q) => {
        const needle = q.trim().toLowerCase();
        if (!needle) return bundle.entries;
        return bundle.entries.filter(
          (e) => e.title.toLowerCase().includes(needle) || e.body.toLowerCase().includes(needle),
        );
      },
      getFlow: (flowKey) => ({
        flowKey,
        steps: bundle.coachmarks.filter((c) => c.flowKey === flowKey).sort((a, b) => a.stepOrder - b.stepOrder),
        seen: seen.includes(flowKey),
      }),
      markSeen: (flowKey) => {
        setSeen((prev) => (prev.includes(flowKey) ? prev : [...prev, flowKey]));
        void markFlowSeen(flowKey);
      },
      resetSeen: () => {
        setSeen([]);
        void resetSeenFlows();
      },
      seenLoaded,
    };
  }, [bundle, seen, seenLoaded]);

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used within <HelpProvider>. Mount it in app/_layout.tsx.');
  return ctx;
}

/** Convenience: the contextual entry for a screen (+ optional state). */
export function useHelpEntry(screenKey: string, stateKey?: string): HelpEntry | undefined {
  return useHelp().getEntry(screenKey, stateKey);
}
